import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { CheckCircle2 } from 'lucide-react';
import { googleSignupSchema, signupSchema, TERMS_VERSION, PRIVACY_VERSION, type Trade } from '@worki/shared';
import { useForm } from '../lib/useForm.js';
import { isAdmitted, useSession } from '../lib/session.js';
import { api, RequestError } from '../lib/api.js';
import { Button, ErrorNotice, Field, FIELD, Input, Select, Spinner } from '../components/ui.js';
import { AuthShell } from '../components/AuthShell.js';
import { CityInput } from '../components/CityInput.js';
import { GoogleButton } from '../components/GoogleButton.js';
import { cn } from '../lib/cn.js';

/**
 * AUTH-001/AUTH-010 — opening an account.
 *
 * Two ways in, one form. By email it is two steps: the details are emailed a
 * confirmation code and the account only exists once that code comes back, so
 * the address is proven before it owns a business. Through Google the address
 * arrives already proven, and the same fields are asked for once.
 *
 * The four business details are not optional either way. A trade off the list
 * Worki publishes, a mobile and a company number are what make the account a
 * business rather than an inbox — and the last two are unique across Worki, so
 * the same company cannot be opened twice from two addresses.
 */
export function SignupPage() {
  const { t } = useTranslation();
  const { verifySignup, completeGoogleSignup } = useSession();
  const navigate = useNavigate();
  const [params] = useSearchParams();

  /** Present only on the way back from Google, and only until it is spent. */
  const googleToken = params.get('google');

  const [step, setStep] = useState<'details' | 'code' | 'pending'>('details');
  const [code, setCode] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [codeError, setCodeError] = useState('');

  const form = useForm(googleToken ? googleSignupSchema : signupSchema, {
    businessName: '',
    name: '',
    email: '',
    phone: '',
    tradeId: '',
    taxId: '',
    city: '',
    acceptedTerms: false,
    termsVersion: TERMS_VERSION,
    privacyVersion: PRIVACY_VERSION,
  });
  const email = String(form.values.email ?? '');

  /** The list is Worki's, not free text: an unknown trade is refused on save. */
  const trades = useQuery({
    queryKey: ['trades'],
    queryFn: () => api.get<{ items: Trade[] }>('/auth/trades'),
    staleTime: Infinity,
  });

  const pending = useQuery({
    queryKey: ['google-signup', googleToken],
    queryFn: () => api.get<{ email: string; name: string }>(`/auth/google/pending?token=${encodeURIComponent(googleToken!)}`),
    enabled: Boolean(googleToken),
    retry: false,
  });

  const { setValue } = form;
  const handedOver = pending.data;
  useEffect(() => {
    if (!handedOver) return;
    setValue('email', handedOver.email);
    // Google knows what the person is called; it does not know their business.
    if (handedOver.name) setValue('name', handedOver.name);
  }, [handedOver, setValue]);

  /*
   * MKT-002 — arriving from the landing page's sign-in card, which has the
   * address already typed. Carried in the query rather than asked for twice;
   * it is a starting value and not a proven one, so the code round-trip below
   * still has to happen before an account exists.
   */
  const suggestedEmail = params.get('email');
  useEffect(() => {
    if (suggestedEmail) setValue('email', suggestedEmail);
  }, [suggestedEmail, setValue]);

  const handleDetails = (event: React.FormEvent) => {
    event.preventDefault();
    void form.submit(
      async (data) => {
        if (googleToken) {
          const answer = await completeGoogleSignup(data as Parameters<typeof completeGoogleSignup>[0]);
          if (isAdmitted(answer)) navigate('/', { replace: true });
          else setStep('pending');
          return;
        }
        await api.post('/auth/signup', data);
        setStep('code');
      },
      (values) => (googleToken ? { ...values, token: googleToken } : values),
    );
  };

  const handleCode = async (event: React.FormEvent) => {
    event.preventDefault();
    setVerifying(true);
    setCodeError('');
    try {
      const answer = await verifySignup({ email, code });
      if (isAdmitted(answer)) navigate('/', { replace: true });
      else setStep('pending');
    } catch (error) {
      setCodeError(error instanceof RequestError ? error.code : 'errors.unknown');
    } finally {
      setVerifying(false);
    }
  };

  const field = (name: string, label: string, type = 'text', autoComplete?: string) => (
    <Field label={label} htmlFor={name} {...(form.errors[name] ? { error: t(form.errors[name]) } : {})}>
      <Input
        id={name}
        type={type}
        {...(autoComplete ? { autoComplete } : {})}
        {...(type === 'email' || type === 'tel' ? { dir: 'ltr' as const } : {})}
        value={String(form.values[name] ?? '')}
        invalid={Boolean(form.errors[name])}
        onChange={(event) => form.setValue(name, event.target.value)}
      />
    </Field>
  );

  /*
   * 1.34 — the account exists and waits for Worki to open it. That is an answer
   * and not a failure, so it is not drawn as one. Ahead of the expired-link
   * branch below on purpose: a Google handoff is spent by now, and asking about
   * it again would replace this with "the link expired".
   */
  if (step === 'pending') {
    return (
      <AuthShell
        title={t('auth.signupPendingTitle')}
        subtitle={t('auth.signupPendingSubtitle', { email })}
        onSubmit={(event) => event.preventDefault()}
        footer={
          <>
            {t('auth.haveAccount')}{' '}
            <Link to="/login" className="text-primary font-semibold hover:underline">
              {t('auth.login')}
            </Link>
          </>
        }
      >
        <SignupPending />
      </AuthShell>
    );
  }

  // The handoff expires, and a stale link must say so rather than silently
  // dropping the visitor into an ordinary sign-up with Google's name in it.
  if (googleToken && pending.isError) {
    return (
      <AuthShell
        title={t('auth.signupTitle')}
        subtitle={t('auth.signupSubtitle')}
        onSubmit={(event) => event.preventDefault()}
        footer={
          <>
            {t('auth.haveAccount')}{' '}
            <Link to="/login" className="text-primary font-semibold hover:underline">
              {t('auth.login')}
            </Link>
          </>
        }
      >
        <ErrorNotice code="errors.googleExpired" />
        <Button type="button" variant="primary" size="lg" block onClick={() => navigate('/signup', { replace: true })}>
          {t('auth.signup')}
        </Button>
      </AuthShell>
    );
  }

  return (
    <AuthShell
      wide={step === 'details'}
      title={googleToken ? t('auth.googleTitle') : t('auth.signupTitle')}
      subtitle={
        step === 'code'
          ? t('auth.signupCodeSent', { email })
          : googleToken
            ? t('auth.googleSubtitle', { email: pending.data?.email ?? '' })
            : t('auth.signupSubtitle')
      }
      onSubmit={step === 'details' ? handleDetails : handleCode}
      footer={
        <>
          {t('auth.haveAccount')}{' '}
          <Link to="/login" className="text-primary font-semibold hover:underline">
            {t('auth.login')}
          </Link>
        </>
      }
    >
      {step === 'details' ? (
        <>
          {form.formError && <ErrorNotice code={form.formError} />}

          {googleToken ? (
            pending.isLoading && (
              <div className="flex justify-center py-2">
                <Spinner />
              </div>
            )
          ) : (
            <GoogleButton returnTo="/" />
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {field('businessName', t('auth.businessName'), 'text', 'organization')}
            {field('name', t('auth.name'), 'text', 'name')}
            {/* Already proven when Google handed the visitor over, so it is
                shown as a fact rather than offered for editing — changing it
                here would not change which address was verified. */}
            {googleToken
              ? null
              : field('email', t('auth.email'), 'email', 'email')}
            {field('phone', t('auth.phone'), 'tel', 'tel')}
            <Field
              label={t('auth.trade')}
              htmlFor="tradeId"
              {...(form.errors.tradeId ? { error: t(form.errors.tradeId) } : {})}
            >
              {/* Searchable because there are thirty-five of them and the first
                  letter is a poor index into a Hebrew list: שיפוצים, שיווק
                  דיגיטלי and שיש ואבן all begin the same way, so the typeahead
                  the dropdown already had lands on the wrong one. */}
              <Select
                id="tradeId"
                value={String(form.values.tradeId ?? '')}
                onChange={(value) => form.setValue('tradeId', value)}
                placeholder={t('auth.tradePick')}
                invalid={Boolean(form.errors.tradeId)}
                searchable
                searchPlaceholder={t('auth.tradeSearch')}
                emptyText={t('auth.tradeNoMatch')}
                className="h-10 px-3"
                options={(trades.data?.items ?? []).map((trade) => ({ value: trade.id, label: trade.name }))}
              />
            </Field>
            <Field
              label={t('auth.taxId')}
              htmlFor="taxId"
              {...(form.errors.taxId ? { error: t(form.errors.taxId) } : { hint: t('auth.taxIdHint') })}
            >
              <Input
                id="taxId"
                inputMode="numeric"
                dir="ltr"
                maxLength={9}
                value={String(form.values.taxId ?? '')}
                invalid={Boolean(form.errors.taxId)}
                onChange={(event) => form.setValue('taxId', event.target.value.replace(/\D/g, '').slice(0, 9))}
              />
            </Field>
            {/*
              The only optional field on the form, and said so in the hint: it
              spares typing on every later quote and lead rather than proving
              anything about the business. Same suggesting-not-deciding input
              the lead form uses, so a moshav that is not on the register can
              still be written in full.
            */}
            <Field label={t('auth.city')} htmlFor="city" hint={t('auth.cityHint')}>
              <CityInput
                id="city"
                value={String(form.values.city ?? '')}
                onChange={(value) => form.setValue('city', value)}
                placeholder={t('auth.city')}
                aria-label={t('auth.city')}
                className={cn(FIELD, 'h-10 px-3')}
              />
            </Field>
          </div>

          <div className="space-y-1.5">
            {/*
              LEGAL-001 — the two documents are named here as links, so the box
              cannot be ticked without the text it confirms being one click
              away. The label is a sibling rather than a wrapper because a link
              inside a label is a control inside a control; browsers skip label
              activation for interactive descendants, but the flatter markup
              does not rely on that. Same tab on purpose: `target="_blank"` is
              unreliable inside the Capacitor WebView, and a link that silently
              does nothing is worse than losing a short form.
            */}
            <div className="flex items-start gap-2.5 text-xs text-muted-foreground">
              <input
                id="accepted-terms"
                type="checkbox"
                checked={Boolean(form.values.acceptedTerms)}
                onChange={(event) => form.setValue('acceptedTerms', event.target.checked)}
                aria-invalid={form.errors.acceptedTerms ? 'true' : undefined}
                className="mt-0.5 h-4 w-4 shrink-0 rounded accent-primary"
              />
              <label htmlFor="accepted-terms" className="leading-relaxed cursor-pointer">
                {t('auth.termsPrefix')}
                <Link to="/terms" className="text-primary underline underline-offset-2">
                  {t('auth.termsLink')}
                </Link>
                {t('auth.termsAnd')}
                <Link to="/privacy" className="text-primary underline underline-offset-2">
                  {t('auth.privacyLink')}
                </Link>
              </label>
            </div>
            {form.errors.acceptedTerms && (
              <span className="block text-xs text-destructive">{t(form.errors.acceptedTerms)}</span>
            )}
          </div>

          <Button type="submit" variant="primary" size="lg" block loading={form.submitting}>
            {t('auth.signup')}
          </Button>
        </>
      ) : (
        <>
          {codeError && <ErrorNotice code={codeError} />}
          <Field label={t('auth.otp')} htmlFor="code" hint={t('auth.codeExpiry')}>
            <Input
              id="code"
              autoFocus
              inputMode="numeric"
              autoComplete="one-time-code"
              dir="ltr"
              maxLength={6}
              value={code}
              onChange={(event) => setCode(event.target.value.replace(/\D/g, '').slice(0, 6))}
            />
          </Field>
          <Button
            type="submit"
            variant="primary"
            size="lg"
            block
            loading={verifying}
            disabled={code.length !== 6}
          >
            {t('auth.confirmSignup')}
          </Button>
          <Button
            type="button"
            variant="ghost"
            block
            onClick={() => {
              setStep('details');
              setCode('');
              setCodeError('');
            }}
          >
            {t('auth.editDetails')}
          </Button>
        </>
      )}
    </AuthShell>
  );
}

/**
 * 1.34 — "the account was created and is waiting for Worki", after a sign-up that
 * worked. A calm card with a check mark: what is left is Worki's to do and not the
 * visitor's. It was the red `ErrorNotice`, and read as a sign-up that had failed.
 * The words are the ones it always carried; it is a `status` and not an `alert`,
 * since nothing here asks to be fixed.
 */
export function SignupPending() {
  const { t } = useTranslation();
  return (
    <div role="status" className="flex items-start gap-3 rounded-xl border border-success/30 bg-success/10 p-4">
      <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-success" aria-hidden />
      <p className="text-base leading-relaxed text-foreground">{t('errors.accountPending')}</p>
    </div>
  );
}
